// "Wrong queue" prompt shared by the put-away screens: names the queue the
// scanned tote belongs to and offers to take the user there. Rendered
// through a portal so each screen can drop it into any render branch.
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { useTranslation } from "../../i18n";
import type { QueueKey, DropQueueInfo } from "./toteWhereabouts";
import { queuePath } from "./toteWhereabouts";

interface Props {
    toteId: string;
    queue: QueueKey;
    info: DropQueueInfo | null;
    onGo: (path: string) => void;
    onClose: () => void;
}

const QUEUE_TITLE_KEY: Record<QueueKey, string> = {
    shelf: "shelf_drop",
    forecast: "forecast_drop",
    put_away: "put_away",
    picking: "picking",
};

const WrongQueueModal = ({ toteId, queue, info, onGo, onClose }: Props) => {
    const { t } = useTranslation();
    const path = queuePath(queue);
    const queueName = t(QUEUE_TITLE_KEY[queue]);
    const body = queue === "picking"
        ? t("wrong_queue_picking", { tote: toteId })
        : queue === "put_away"
            ? t("wrong_queue_put_away", { tote: toteId })
            : t("wrong_queue_drop", {
                tote: toteId,
                queue: queueName,
                units: String(info?.units ?? 0),
                name: info?.pickedBy ?? "?",
            });

    return createPortal(
        <div className="modal-overlay book-in-modal-overlay" onClick={onClose}>
            <div
                className="modal-card book-in-reopen-modal"
                data-testid="wrong-queue-modal"
                onClick={e => e.stopPropagation()}
            >
                <div className="modal-card-header">
                    <h2>{t("wrong_queue_title")}</h2>
                    <button type="button" className="modal-close" onClick={onClose} aria-label={t("close")}>
                        <X size={18} />
                    </button>
                </div>
                <div className="modal-card-body">
                    <div className="book-in-close-card">
                        <div className="book-in-close-card-id">{toteId}</div>
                    </div>
                    <p className="book-in-close-hint">{body}</p>
                    <div className="modal-confirm-buttons book-in-reopen-buttons">
                        <button type="button" className="btn btn-ghost btn-lg" onClick={onClose}>
                            {t("wrong_queue_stay")}
                        </button>
                        {path && (
                            <button type="button" className="btn btn-primary btn-lg" onClick={() => onGo(path)}>
                                {t("wrong_queue_go", { queue: queueName })}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default WrongQueueModal;
